/* eslint-disable no-shadow */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import { Provider, connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  AdvancedMarker,
  InfoWindow,
  Map,
  useMap,
} from '@vis.gl/react-google-maps';

import haversine from 'haversine';
import {
  Button,
  getTranslation,
  Icon,
  ListIcon,
  LocationServicesIcon,
  TranslationProvider,
} from '@resy/ui-kitchen';

import './MapContainer.scss';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import MarkerIcon from '../../svg/marker-red.svg';
import NoResults from '../../components/NoResults';
import Popover from '../../components/Popover';
import { getMarketLocationThunk, setSelectedLocationThunk } from '../../../../../ui-kitchen/src/reducers/resyLocationsSlice';
import { usePrevious } from '../../components/VenuePage/usePrevious';
import { LIST_ID } from '../../../../../ui-kitchen/src/components/SearchFilterContainer/searchFilterConstants';
import SearchFilterContainer from '../../../../../ui-kitchen/src/components/SearchFilterContainer/SearchFilterContainer';

const FACET_TYPE = { list: LIST_ID };

const MapContainer = ({
  $rootScope,
  $window,
  Analytics,
  cuisines,
  defaultCenter,
  error,
  getMarketLocationThunk,
  hasGeolocationControlBeenClicked,
  hits,
  hoveredVenue,
  isAuthenticated,
  isActive,
  isGeolocationActive,
  isGeolocationBlocked,
  isGeolocationLoading,
  isGeolocationSupported,
  isLoading,
  isMobile,
  isSearching,
  listCount,
  lists,
  nearbyTranslation,
  onActiveViewToggle,
  onClear,
  onExpandToVenues,
  onExpandMap,
  onGeolocationActiveChange,
  onGeolocationClick,
  onInfoBoxSelect,
  onMapChange,
  onMarkerSelect,
  onRedo,
  query,
  seating,
  selectedDate,
  selectedLocation,
  selectedPartySize,
  selectedTime,
  selectedVenueId,
  setSelectedLocationThunk,
  shouldCenterOnUser,
  shouldFitBounds,
  shouldShowRedo,
  urlSlug,
  userLocation,

}) => {
  const defaultOptions = {
    clickableIcons: false,
    defaultCenter,
    fullscreenControl: false,
    gestureHandling: 'greedy',
    mapId: 'a6325cfd8b5563cfdebf3268',
    mapTypeControl: false,
    maxZoom: 18,
    streetViewControl: false,
    defaultZoom: 12,
  };

  const infoBoxOptions = {
    alignBottom: true,
    closeBoxURL: '',
    shouldFocus: true,
    enableEventPropagation: true,
    headerDisabled: true,
    infoBoxClearance: {
      height: 50,
      width: 50,
    },
    pixelOffset: {
      height: -60,
    },
  };

  const [preventGeolocationActiveChange, setPreventGeolocationActiveChange] = useState(false);
  const map = useMap();
  const [mapLib, setMapLib] = useState(map);

  const prevIsActive = usePrevious(isActive);
  const prevHits = usePrevious(hits);
  const prevMapLib = usePrevious(mapLib);
  const mappableHits = hits.filter((hit) => (
    Number.isFinite(hit?._geoloc?.lat) && Number.isFinite(hit?._geoloc?.lng) // eslint-disable-line no-underscore-dangle
  ));
  /**
   * 80 meters was chosen because a quick read indicates this is the smaller "standard" for a city block
   * Reference: https://en.wikipedia.org/wiki/City_block#Grid_plan
   */
  const didCoordinatesChange = (start, end, threshold, unit) => !haversine(start, end, { threshold: (threshold || 80), unit: (unit || 'meter') });

  const fitBounds = async () => {
    if (!mapLib || mappableHits.length === 0) {
      return;
    }

    let bounds;
    try {
      bounds = new google.maps.LatLngBounds();
    } catch (err) {
      Logger.error('[MapContainer] Error:', err);
      const { LatLngBounds } = await google.maps.importLibrary('core');
      bounds = new LatLngBounds();
    }

    mappableHits.forEach((hit) => bounds.extend(hit._geoloc)); // eslint-disable-line no-underscore-dangle
    mapLib.fitBounds(bounds);
  };

  const isWithinLocationRadius = (coordinates) => {
    const { latitude, longitude } = selectedLocation.map_center;
    const selectedLocationCenter = { latitude, longitude };
    return !didCoordinatesChange(selectedLocationCenter, coordinates, selectedLocation.radius, 'mile');
  };

  const handleGeolocationControlClick = () => {
    if (hasGeolocationControlBeenClicked) {
      if (!isGeolocationBlocked) {
        onGeolocationActiveChange();
        setPreventGeolocationActiveChange(true);
      }
      if (userLocation && mapLib) {
        const coordinates = {
          latitude: userLocation.lat,
          longitude: userLocation.lng,
        };
        if (isWithinLocationRadius(coordinates)) {
          mapLib.panTo(userLocation);
        } else {
          getMarketLocationThunk({ coordinates: userLocation })
            .then(
              (location) => {
                const params = { $rootScope, $window };
                if (location) {
                  params.location = location;
                  setSelectedLocationThunk(params);
                } else {
                  params.location = { ...selectedLocation, name: nearbyTranslation, translationKey: 'nearby' };
                  setSelectedLocationThunk(params);
                  mapLib.panTo(userLocation);
                }
              },
              () => { }
            );
        }
      }
    } else {
      onGeolocationClick();
    }
  };

  const onIdle = () => {
    if (mapLib) {
      onMapChange(mapLib.getBounds(), mapLib.getZoom(), preventGeolocationActiveChange, false);
    }
    setPreventGeolocationActiveChange(false);
  };

  const onMapClick = () => {
    onMarkerSelect(null);
  };

  const onDragEnd = () => {
    onMarkerSelect(null);
  };

  const escKeyListener = (event) => {
    if (event.key === 'Escape') {
      onMarkerSelect(null);
      window.removeEventListener('keydown', escKeyListener);
    }
  };

  const onMarkerClick = (venue) => {
    onMarkerSelect(venue);
    window.addEventListener('keydown', escKeyListener);
  };

  const renderMobileFilters = () => (
    <div className="MapContainer__filters" data-test-id="mobile_map_container_filter">
      <SearchFilterContainer
        $rootScope={$rootScope}
        Analytics={Analytics}
        cuisines={cuisines}
        isAuthenticated={isAuthenticated}
        isMobile={isMobile}
        lists={lists}
        seating={seating}
        urlSlug={urlSlug}
      />
    </div>
  );

  const renderActiveViewToggle = () => {
    if (isMobile) {
      return (
        <Button
          className="ActiveViewToggle"
          type="button"
          onClick={onActiveViewToggle}
          data-test-id="mobile_active_view_toggle"
        >
          <Icon component={ListIcon} name="list" />
          <TranslationProvider translationKey="list">
            List
          </TranslationProvider>
        </Button>
      );
    }
    return null;
  };

  const renderError = () => {
    if (error) {
      return (
        <div
          className={cx('Error__container', {
            'Error__container--hide': error.shouldHide,
          })}
        >
          <div className="Error">
            {error.message || 'Something went wrong. Please try again.'}
          </div>
        </div>
      );
    }
    return null;
  };

  const renderGeolocationControl = () => {
    if (isGeolocationSupported) {
      return (
        <button
          data-test-id="geolocation_button"
          className={cx('GeolocationControl', {
            'GeolocationControl--active': isGeolocationActive,
            'GeolocationControl--blocked': isGeolocationBlocked,
            'GeolocationControl--loading': isGeolocationLoading,
          })}
          type="button"
          tabIndex={0}
          aria-label={getTranslation({
            translationKey: 'current_location',
            fallback: 'Current Location',
          })}
          onClick={handleGeolocationControlClick}
        >
          <Icon component={LocationServicesIcon} name="LocationServices" />
        </button>
      );
    }
    return null;
  };

  const renderMarkers = () => mappableHits.map((hit) => (
    <AdvancedMarker
      data-test-id={`marker_${hit.id}`}
      key={hit.id}
      aria-label={hit.name}
      title={hit.name}
      labelAnchor={{ x: 20, y: 40 }}
      labelClass={cx('MarkerIcon__container', {
        'MarkerIcon__container--active': hoveredVenue === hit.id || selectedVenueId === hit.id,
      })}
      onClick={() => onMarkerClick(hit)}
      opacity={0}
      position={hit._geoloc} // eslint-disable-line no-underscore-dangle
      tabIndex={0}
    >
      <>
        <MarkerIcon
          className={cx('MarkerIcon', {
            'MarkerIcon--rga': !!hit.isGda,
            'MarkerIcon--active': hoveredVenue === hit.id || selectedVenueId === hit.id,
          })}
        />
        {selectedVenueId === hit.id && (
          <InfoWindow
            data-test-id={`info_window_${hit.id}`}
            position={new google.maps.LatLng(hit._geoloc.lat, hit._geoloc.lng)} // eslint-disable-line no-underscore-dangle
            options={infoBoxOptions}
          >
            <div
              onClick={() => onInfoBoxSelect(hit)}
              onKeyPress={() => onInfoBoxSelect(hit)}
            >
              <Provider store={$rootScope.reduxStore}>
                <Popover
                  isMobile={isMobile}
                  selectedDate={selectedDate}
                  selectedPartySize={selectedPartySize}
                  selectedTime={selectedTime}
                  venue={hit}
                />
              </Provider>
              <div className="InfoBox__pointer" />
            </div>
          </InfoWindow>
        )}
      </>
    </AdvancedMarker>
  ));

  const renderNoResults = () => {
    if (isMobile && !isSearching && hits.length === 0) {
      return <NoResults onClear={onClear} query={query} />;
    }
    return null;
  };

  const renderRedo = () => {
    if (shouldShowRedo) {
      return (
        <TranslationProvider translationKey="map_redo">
          <button
            className={cx('update-map', {
              'update-map-mobile': isMobile,
            })}
            onClick={() => {
              onRedo(true);
              if (onMapChange) {
                onMapChange(mapLib.getBounds(), mapLib.getZoom(), preventGeolocationActiveChange, true);
              }
            }}
            type="button"
          >
            Search Here
          </button>
        </TranslationProvider>
      );
    }
    return null;
  };

  const renderExpandButton = () => {
    const { search } = window.location;
    const isListFilter = (search.indexOf(FACET_TYPE.list) > -1);
    if (!shouldShowRedo && isListFilter && hits.length < listCount && mapLib) {
      return (
        <TranslationProvider translationKey="show_all_venues">
          <Button
            className={cx('update-map', {
              'update-map-mobile': isMobile,
            })}
            onClick={() => onExpandToVenues(mapLib)}
          >
            Show Full List
          </Button>
        </TranslationProvider>
      );
    }

    if (!shouldShowRedo && hits.length <= 5 && mapLib) {
      return (
        <TranslationProvider translationKey="expand_nearby">
          <Button
            className={cx('update-map', {
              'update-map-mobile': isMobile,
            })}
            onClick={() => onExpandMap(mapLib)}
          >
            Expand Nearby
          </Button>
        </TranslationProvider>
      );
    }
    return null;
  };

  const renderUserLocation = () => {
    if (userLocation) {
      return (
        <AdvancedMarker
          labelAnchor={{ x: 10, y: 10 }} // 10 is half the UserLocationMarker size (20)
          labelClass="UserLocationMarker__container"
          opacity={0}
          position={userLocation}
        >
          <div className="UserLocationMarker__outer-circle">
            <div className="UserLocationMarker">
              <div
                className={cx('UserLocationMarker__inner-circle', {
                  // if we want to implement watchPosition and follow the user (mobile)
                  // pulsate: isMobile,
                })}
              />
            </div>
          </div>
        </AdvancedMarker>
      );
    }
    return null;
  };

  useEffect(() => {
    if (mappableHits.length > 0 && shouldFitBounds) {
      fitBounds();
    }
  }, []);

  useEffect(() => {
    if (!mapLib) {
      setMapLib(map);
    }
  }, [map]);

  useEffect(() => {
    const hasHits = mappableHits.length > 0;
    const didHitsChange = JSON.stringify(hits) !== JSON.stringify(prevHits);
    const didActiveStateChange = isActive && isActive !== prevIsActive;
    const didMapInitialize = !prevMapLib && !!mapLib;

    if ((hasHits && didHitsChange) || (didActiveStateChange && hasHits) || (didMapInitialize && hasHits)) {
      // NOTE: catching max zoom to focus on markers
      if (shouldFitBounds || (mapLib && mapLib.getZoom() === 1) || (query?.type === 'query' && query?.value === '')) {
        fitBounds();
      }
      renderMarkers();
    }

    if (shouldCenterOnUser && mapLib) {
      mapLib.panTo(userLocation);
    }
  }, [
    hits, isActive, shouldFitBounds, query?.value, query?.type, mapLib,
  ]);

  return (
    <div className="MapContainer" data-test-id="map_container">
      {(isLoading || (hits.length === 0 && isSearching)) ? (
        <Map
          data-test-id="default_loading_map"
          {...defaultOptions}
        />
      )
        : (
          <Map
            data-test-id="loaded_map"
            {...defaultOptions}
            onClick={onMapClick}
            onDragend={onDragEnd}
            onIdle={onIdle}
          >
            {isMobile && renderMobileFilters()}
            {renderActiveViewToggle()}
            {renderRedo()}
            {renderExpandButton()}
            {renderGeolocationControl()}
            {renderMarkers()}
            {renderNoResults()}
            {renderUserLocation()}
            {renderError()}
          </Map>
        )}
    </div>
  );
};

MapContainer.displayName = 'MapContainer';

const mapStateToProps = ({ favorite: { lists } }) => {
  const { search } = window.location;
  let listCount;
  const isListFilter = (search.indexOf(FACET_TYPE.list) > -1);
  if (isListFilter) {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const facet = params.get('facet');
    const [, listId] = facet.split(':');
    const listById = lists.filter((list) => (list.listId === listId));
    listCount = listById[0]?.count;
  }
  return { listCount };
};

MapContainer.propTypes = {
  $rootScope: PropTypes.shape({
    reduxStore: PropTypes.shape({}).isRequired,
  }).isRequired,
  $window: PropTypes.shape({}).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  cuisines: PropTypes.arrayOf(PropTypes.object),
  defaultCenter: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }).isRequired,
  error: PropTypes.shape({
    code: PropTypes.number.isRequired,
    message: PropTypes.string.isRequired,
    shouldHide: PropTypes.bool,
  }),
  getMarketLocationThunk: PropTypes.func.isRequired,
  hasGeolocationControlBeenClicked: PropTypes.bool.isRequired,
  hits: PropTypes.arrayOf(PropTypes.object).isRequired,
  hoveredVenue: PropTypes.number,
  isAuthenticated: PropTypes.bool,
  isActive: PropTypes.bool.isRequired,
  isGeolocationActive: PropTypes.bool.isRequired,
  isGeolocationBlocked: PropTypes.bool.isRequired,
  isGeolocationLoading: PropTypes.bool.isRequired,
  isGeolocationSupported: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  isSearching: PropTypes.bool.isRequired,
  listCount: PropTypes.number,
  lists: PropTypes.arrayOf(PropTypes.object),
  nearbyTranslation: PropTypes.string.isRequired,
  onActiveViewToggle: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onExpandToVenues: PropTypes.func.isRequired,
  onExpandMap: PropTypes.func.isRequired,
  onGeolocationActiveChange: PropTypes.func.isRequired,
  onGeolocationClick: PropTypes.func.isRequired,
  onInfoBoxSelect: PropTypes.func.isRequired,
  onMapChange: PropTypes.func.isRequired,
  onMarkerSelect: PropTypes.func.isRequired,
  onRedo: PropTypes.func.isRequired,
  query: PropTypes.shape({
    type: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  seating: PropTypes.arrayOf(PropTypes.object),
  selectedDate: PropTypes.string,
  selectedLocation: PropTypes.shape({
    code: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    map_center: PropTypes.shape({
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
    }),
    name: PropTypes.string.isRequired,
    radius: PropTypes.number.isRequired,
  }).isRequired,
  selectedPartySize: PropTypes.number,
  selectedTime: PropTypes.string,
  selectedVenueId: PropTypes.number,
  setSelectedLocationThunk: PropTypes.func.isRequired,
  shouldCenterOnUser: PropTypes.bool.isRequired,
  shouldFitBounds: PropTypes.bool.isRequired,
  shouldShowRedo: PropTypes.bool.isRequired,
  urlSlug: PropTypes.string.isRequired,
  userLocation: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
};

MapContainer.defaultProps = {
  cuisines: undefined,
  error: null,
  hoveredVenue: null,
  isAuthenticated: false,
  isMobile: true,
  listCount: undefined,
  lists: undefined,
  query: null,
  seating: undefined,
  selectedDate: null,
  selectedPartySize: null,
  selectedTime: undefined,
  selectedVenueId: null,
  userLocation: null,
};

const mapDispatchToProps = {
  getMarketLocationThunk, setSelectedLocationThunk,
};
const WrappedMapContainer = MapContainer;

export { MapContainer as MapContainerForTesting };
export default connect(mapStateToProps, mapDispatchToProps)(WrappedMapContainer);
